import type { SVGProps } from 'react';

export type IconName =
  | 'list'
  | 'search'
  | 'megaphone'
  | 'person'
  | 'chevron-down'
  | 'chevron-right'
  | 'wallet'
  | 'badge-check'
  | 'calendar-check'
  | 'box-arrow-right'
  | 'speedometer'
  | 'beaker'
  | 'graph-up'
  | 'type'
  | 'gift'
  | 'gear'
  | 'life-preserver'
  | 'chevron-left'
  | 'stars'
  | 'box-arrow-up-right'
  | 'caret-left'
  | 'caret-left-fill'
  | 'bezier'
  | 'buildings'
  | 'journals'
  | 'layout'
  | 'newspaper'
  | 'arrow-up-right'
  | 'collection'
  | 'lightning'
  | 'folder';

type IconProps = {
  name: IconName;
  size?: number;
  className?: string;
  strokeWidth?: number;
} & Omit<SVGProps<SVGSVGElement>, 'viewBox'>;

const DEFAULT_SIZE = 16;
const DEFAULT_STROKE = 1.5;

export function Icon({ name, size = DEFAULT_SIZE, className, strokeWidth = DEFAULT_STROKE, ...rest }: IconProps) {
  const shared = {
    width: size,
    height: size,
    viewBox: '0 0 16 16',
    className: className ? `panel-icon ${className}` : 'panel-icon',
    xmlns: 'http://www.w3.org/2000/svg',
    ...rest,
  } as const;

  switch (name) {
    case 'list':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round">
          <path d="M3 4.5h10" />
          <path d="M3 8h10" />
          <path d="M3 11.5h10" />
        </svg>
      );
    case 'search':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
          <circle cx={7} cy={7} r={4.5} />
          <path d="M10.5 10.5 14 14" />
        </svg>
      );
    case 'megaphone':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinejoin="round" strokeLinecap="round">
          <path d="M2.5 6v4.5a1 1 0 0 0 1 1h1l2.5 1.5V3.5L4.5 5h-1a1 1 0 0 0-1 1Z" />
          <path d="M9 4.5 13 3v10l-4-1.5" />
        </svg>
      );
    case 'person':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
          <circle cx={8} cy={5} r={2.5} />
          <path d="M3.5 13.5c.7-2.2 2.7-3.5 4.5-3.5s3.8 1.3 4.5 3.5" />
        </svg>
      );
    case 'chevron-down':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
          <path d="m3.5 6.5 4.5 4 4.5-4" />
        </svg>
      );
    case 'chevron-right':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
          <path d="m5.5 4 4 4-4 4" />
        </svg>
      );
    case 'wallet':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinejoin="round">
          <rect x={2.25} y={4.5} width={11.5} height={7.5} rx={1.6} />
          <circle cx={10.5} cy={8.25} r={0.9} />
        </svg>
      );
    case 'badge-check':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
          <circle cx={8} cy={8} r={4.5} />
          <path d="m6.2 8 1.3 1.3 2.3-2.3" />
        </svg>
      );
    case 'calendar-check':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinejoin="round" strokeLinecap="round">
          <rect x={2.5} y={4} width={11} height={9.5} rx={1.5} />
          <path d="M5 2.5v3" />
          <path d="M11 2.5v3" />
          <path d="M2.5 6.5h11" />
          <path d="m6.3 9.5 1.5 1.5 2.2-2.2" />
        </svg>
      );
    case 'box-arrow-right':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
          <rect x={2.5} y={4} width={6.5} height={8} rx={1.2} />
          <path d="M9 8h5" />
          <path d="m12.5 5.5 2.5 2.5-2.5 2.5" />
        </svg>
      );
    case 'speedometer':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 12.5a6 6 0 1 1 10 0" />
          <path d="M8 8l2.5 1.5" />
          <circle cx={8} cy={8} r={0.7} fill="currentColor" />
        </svg>
      );
    case 'beaker':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 2.5h6" />
          <path d="M6 2.5v4.5l-2.3 5.2A1 1 0 0 0 4.6 14h6.8a1 1 0 0 0 .9-1.8L10 7V2.5" />
          <path d="M5.5 9h5" />
        </svg>
      );
    case 'graph-up':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
          <path d="M2.5 2.5v11" />
          <path d="M2.5 12.5h11" />
          <path d="m3.7 9.5 3-3 2.2 2.2 3.6-4.8" />
        </svg>
      );
    case 'type':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 4.5h10" />
          <path d="M8 4.5v7" />
          <path d="M5 11.5h6" />
        </svg>
      );
    case 'gift':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
          <rect x={2.5} y={7} width={11} height={6.5} rx={1} />
          <path d="M2.5 7h11" />
          <path d="M8 3v10.5" />
          <path d="M5.8 3a1.6 1.6 0 0 0 0 3.2H8" />
          <path d="M10.2 3a1.6 1.6 0 1 1 0 3.2H8" />
        </svg>
      );
    case 'gear':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
          <circle cx={8} cy={8} r={1.7} />
          <path d="M8 4.2V2.8" />
          <path d="M8 13.2v-1.4" />
          <path d="M4.2 8H2.8" />
          <path d="M13.2 8h-1.4" />
          <path d="m4.9 4.9-1-1" />
          <path d="m12.1 12.1-1-1" />
          <path d="m4.9 11.1-1 1" />
          <path d="m12.1 3.9-1 1" />
        </svg>
      );
    case 'life-preserver':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
          <circle cx={8} cy={8} r={5.5} />
          <circle cx={8} cy={8} r={2.5} />
          <path d="M8 2.5v2" />
          <path d="M8 11.5v2" />
          <path d="M2.5 8h2" />
          <path d="M11.5 8h2" />
          <path d="m4.3 4.3 1.4 1.4" />
          <path d="m10.3 10.3 1.4 1.4" />
          <path d="m4.3 11.7 1.4-1.4" />
          <path d="m10.3 5.7 1.4-1.4" />
        </svg>
      );
    case 'chevron-left':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
          <path d="m10.5 4-4 4 4 4" />
        </svg>
      );
    case 'stars':
      return (
        <svg {...shared} fill="currentColor">
          <path d="M7.657 6.247c.11-.33.576-.33.686 0l.645 1.937a2.89 2.89 0 0 0 1.829 1.828l1.936.645c.33.11.33.576 0 .686l-1.937.645a2.89 2.89 0 0 0-1.828 1.829l-.645 1.936a.361.361 0 0 1-.686 0l-.645-1.937a2.89 2.89 0 0 0-1.828-1.828l-1.937-.645a.361.361 0 0 1 0-.686l1.937-.645a2.89 2.89 0 0 0 1.828-1.828zM3.794 1.148a.217.217 0 0 1 .412 0l.387 1.162c.173.518.579.924 1.097 1.097l1.162.387a.217.217 0 0 1 0 .412l-1.162.387A1.73 1.73 0 0 0 4.593 5.69l-.387 1.162a.217.217 0 0 1-.412 0L3.407 5.69A1.73 1.73 0 0 0 2.31 4.593l-1.162-.387a.217.217 0 0 1 0-.412l1.162-.387A1.73 1.73 0 0 0 3.407 2.31zM10.863.099a.145.145 0 0 1 .274 0l.258.774c.115.346.386.617.732.732l.774.258a.145.145 0 0 1 0 .274l-.774.258a1.16 1.16 0 0 0-.732.732l-.258.774a.145.145 0 0 1-.274 0l-.258-.774a1.16 1.16 0 0 0-.732-.732L9.1 2.137a.145.145 0 0 1 0-.274l.774-.258c.346-.115.617-.386.732-.732z"/>
        </svg>
      );
    case 'box-arrow-up-right':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
          <rect x={2.5} y={4.5} width={8.5} height={8.5} rx={1.5} />
          <path d="M7 9.5 13 3.5" />
          <path d="M9.5 3.5H13v3.5" />
        </svg>
      );
    case 'caret-left':
      return (
        <svg {...shared} fill="currentColor">
          <path d="M10.5 4.5c0-.5-.5-.8-.9-.5L5.5 7.2a.6.6 0 0 0 0 1l4.1 3.2c.4.3.9 0 .9-.5z" />
        </svg>
      );
    case 'caret-left-fill':
      return (
        <svg {...shared} fill="currentColor">
          <path d="M10.5 4.5c0-.5-.5-.8-.9-.5L5.5 7.2a.6.6 0 0 0 0 1l4.1 3.2c.4.3.9 0 .9-.5z" />
        </svg>
      );
    case 'bezier':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
          <circle cx={3.5} cy={11.5} r={1.5} />
          <circle cx={12.5} cy={11.5} r={1.5} />
          <circle cx={8} cy={4.5} r={1.5} />
          <path d="M8 6v3.5" />
          <path d="M5 5.5c1.4-1.3 4.6-1.3 6 0" />
          <path d="M4.7 10c1-.8 2.6-1.5 3.8-1.5s2.8.7 3.8 1.5" />
        </svg>
      );
    case 'buildings':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round">
          <rect x={3} y={5} width={4} height={8} rx={0.8} />
          <rect x={9} y={3} width={4} height={10} rx={0.8} />
          <path d="M4.5 7.5h1" />
          <path d="M4.5 10h1" />
          <path d="M10.5 5.5h1" />
          <path d="M10.5 8h1" />
          <path d="M10.5 10.5h1" />
        </svg>
      );
    case 'journals':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth}>
          <rect x={3} y={3.5} width={8} height={9} rx={1} />
          <path d="M6 5.5h3" />
          <path d="M5 7.5h4" />
          <path d="M5 9.5h4" />
          <path d="M5 11.5h4" />
          <path d="M12 4v8" />
        </svg>
      );
    case 'layout':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth}>
          <rect x={2.5} y={3.5} width={11} height={9} rx={1} />
          <path d="M2.5 6.5h11" />
          <path d="M7.5 12.5v-6" />
        </svg>
      );
    case 'newspaper':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth}>
          <rect x={2.5} y={3.5} width={11} height={9.5} rx={1} />
          <path d="M6.5 5.5h4" />
          <path d="M5 7.5h6" />
          <path d="M5 9.5h6" />
          <path d="M5 11.5h4" />
        </svg>
      );
    case 'arrow-up-right':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
          <path d="M3.5 12.5 12.5 3.5" />
          <path d="M7.5 3.5h5v5" />
        </svg>
      );
    case 'collection':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth}>
          <rect x={2.5} y={3.5} width={11} height={9} rx={1} />
          <path d="M5 6.5h6" />
          <path d="M5 9h6" />
          <path d="M5 11.5h6" />
        </svg>
      );
    case 'lightning':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
          <path d="M9.5 2.5 4.5 8h3l-1 5.5 5-5.5h-3Z" />
        </svg>
      );
    case 'folder':
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
          <path d="M2.5 5.5a1.5 1.5 0 0 1 1.5-1.5h2l1 1h4a1.5 1.5 0 0 1 1.5 1.5v5a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 2.5 11V5.5Z" />
        </svg>
      );
    default:
      return (
        <svg {...shared} fill="none" stroke="currentColor" strokeWidth={strokeWidth}>
          <circle cx={8} cy={8} r={6.5} strokeDasharray="2 2" />
        </svg>
      );
  }
}

export default Icon;
